import { useState, useEffect } from "react";
import { FolderHeart, Clapperboard, Image, Play, Link, Loader2, Youtube, Facebook, Instagram, Trash2, AlertTriangle } from "lucide-react"; 
import { motion, AnimatePresence } from "framer-motion"; // Custom popup ke animations ke liye

interface MediaItem {
  id: number;
  title: string;
  type: 'video' | 'image';
  storage_type: 'file' | 'link';
  url: string;
  thumbnail_url: string | null;
  created_at: string;
}

const Library = () => {
  const [mediaType, setMediaType] = useState<'video' | 'image'>('video');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Custom Delete Modal States
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // const backendUrl = "http://localhost:1415";
  // const backendUrl = "http://localhost/seva-backend";
  const backendUrl = "https://www.sevaafoundation.com/seva-backend";

  useEffect(() => {
    fetchMediaList();
  }, [mediaType]);

  const fetchMediaList = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("sevaa_admin_token");
    try {
      const response = await fetch(`${backendUrl}/api/media/list.php?type=${mediaType}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = await response.json();
      if (data.success) { 
        setMediaItems(data.media); 
      }
    } catch (err) { 
      console.error(err); 
    } finally { 
      setIsLoading(false); 
    }
  };

  // --- Pehle wala window.confirm hata diya, ab ye function custom modal se call hoga ---
  const confirmDelete = async () => {
    if (itemToDelete === null) return;
    
    setIsDeleting(true);
    const token = localStorage.getItem("sevaa_admin_token");
    
    try {
      const response = await fetch(`${backendUrl}/api/media/delete.php?id=${itemToDelete}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMediaItems(prev => prev.filter(item => item.id !== itemToDelete));
        setItemToDelete(null); // Delete hone ke baad modal band kar do
      } else {
        alert(data.message || "Failed to delete");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong while deleting");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderMediaPreview = (item: MediaItem) => {
    if (item.type === 'image') {
      const src = item.storage_type === 'file' ? `${backendUrl}${item.url}` : item.url;
      return <img src={src} className="w-full h-48 object-cover rounded-t-xl" alt={item.title} />;
    } else { 
      if (item.storage_type === 'file') {
        return (
          <div className="w-full h-48 bg-slate-100 rounded-t-xl flex items-center justify-center text-slate-400 group-hover:bg-slate-200 transition-colors">
            <Play className="w-16 h-16 opacity-50"/>
            <span className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/60 text-white text-xs font-semibold rounded-lg">Uploaded File</span>
          </div>
        );
      } else { 
        const srcUrl = item.url.toLowerCase();
        let Icon = <Link className="w-10 h-10 text-slate-400"/>;
        let platformText = "Link";

        if (item.thumbnail_url) { 
          return (
            <div className="relative w-full h-48 rounded-t-xl overflow-hidden group">
              <img src={item.thumbnail_url} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" alt={item.title}/>
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center"><Play className="w-16 h-16 text-white shadow-sm"/></div>
              <Youtube className="absolute top-3 right-3 w-8 h-8 text-white p-1.5 bg-red-600 rounded-full shadow-md"/>
            </div>
          );
        } else { 
          if (srcUrl.includes('instagram')) { Icon = <Instagram className="w-10 h-10 text-pink-600"/>; platformText = "Instagram"; }
          if (srcUrl.includes('facebook')) { Icon = <Facebook className="w-10 h-10 text-blue-700"/>; platformText = "Facebook"; }
          
          return (
            <div className="relative w-full h-48 bg-slate-100 rounded-t-xl flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <Play className="absolute w-16 h-16 text-white opacity-40"/>
                {Icon}
                <span className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/60 text-white text-xs font-semibold rounded-lg">{platformText}</span>
            </div>
          );
        }
      }
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen relative">
      
      {/* CUSTOM DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {itemToDelete !== null && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            {/* Background Blur Overlay */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isDeleting && setItemToDelete(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            
            {/* Modal Box */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm text-center border border-slate-100"
            >
              <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5 border-[6px] border-red-50/50">
                <AlertTriangle size={36} strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Media?</h3>
              <p className="text-slate-500 text-sm mb-8 font-medium">Are you sure you want to delete this file? This action cannot be undone.</p>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setItemToDelete(null)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-500/30"
                >
                  {isDeleting ? <Loader2 size={18} className="animate-spin" /> : "Yes, Delete"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* HEADER */}
      <div className="flex items-center gap-3 mb-8 border-b border-slate-200 pb-6">
        <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center shadow-sm">
          <FolderHeart size={24}/>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="text-slate-500 text-sm">Organized center for all files and social media links</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
        <div className="flex gap-2 mb-8 p-1 bg-slate-50 rounded-xl max-w-sm border border-slate-100">
          {['video', 'image'].map(type => (
            <button 
              key={type} 
              onClick={() => setMediaType(type as any)} 
              className={`flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm transition-all shadow-sm ${mediaType === type ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-white border border-transparent"}`}
            >
              {type === 'video' ? <Clapperboard size={16}/> : <Image size={16}/>} 
              {type === 'video' ? "Videos" : "Images"}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="py-20 flex justify-center items-center gap-3 text-slate-500 font-medium">
            <Loader2 className="animate-spin text-orange-500" size={24}/> 
            Loading your media library...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mediaItems.map(item => (
              <div 
                key={item.id} 
                className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-xl hover:border-orange-200 transition-all cursor-pointer group flex flex-col relative" 
                onClick={() => window.open(item.storage_type === 'file' ? `${backendUrl}${item.url}` : item.url, "_blank")}
              >
                
                <div className="relative">
                  {renderMediaPreview(item)}
                  
                  {/* Ab yahan pe click karne se direct delete nahi hoga, Custom Modal open hoga */}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation(); 
                      setItemToDelete(item.id); 
                    }} 
                    className="absolute top-3 left-3 bg-white/90 text-red-500 hover:bg-red-500 hover:text-white p-2.5 rounded-xl shadow-lg backdrop-blur-sm transition-all z-10 opacity-100 md:opacity-0 md:group-hover:opacity-100" 
                    title="Delete Media"
                  >
                     <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                </div>

                <div className="p-4 border-t border-slate-100 flex-1 flex flex-col justify-center">
                  <h4 className="font-bold text-slate-900 text-sm truncate mb-2">{item.title}</h4>
                  <div className="flex items-center gap-1.5 text-slate-400 text-xs font-medium">
                    {item.storage_type === 'file' ? <Clapperboard size={14}/> : <Link size={14}/>}
                    {item.storage_type === 'file' ? "Uploaded file" : "External Link"}
                  </div>
                </div>
              </div>
            ))}
            
            {mediaItems.length === 0 && (
                <div className="md:col-span-2 lg:col-span-3 xl:col-span-4 py-20 flex flex-col items-center justify-center text-slate-500 p-8 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50">
                  {mediaType === 'video' ? <Clapperboard size={48} className="text-slate-300 mb-4"/> : <Image size={48} className="text-slate-300 mb-4"/>}
                  <p className="text-lg font-semibold text-slate-700">No {mediaType === 'video' ? "videos" : "images"} found.</p>
                  <p className="text-sm mt-1">Add some from the 'Add Media' section to get started!</p>
                </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;